import {inject, Injectable} from '@angular/core';
import {UserPreferencesService} from "../user-preferences.service";
import {RecordTableColumnParams} from "../../types/records-table.types";
import {ColumnGroupParams, StickyColumnParams} from "../../types/table-types/sticky-column.types";
import {RecordsTableService} from "../records-table.service";
import {
  defaultLeftPosition,
  defaultLeftPositionWithCheckbox,
  pagesWithTableCheckbox
} from "../../models/table-sticky-config";

@Injectable({
  providedIn: 'root'
})
export class StickyColumnService {
  private userPreferencesService = inject(UserPreferencesService);
  private recordsTableService = inject(RecordsTableService);

  private stickyColumns: RecordTableColumnParams[] = [];

  public toggleColumnStick({ column, allColumns, tableId }: StickyColumnParams & { tableId?: string }): Array<RecordTableColumnParams> {
    column.sticky = !column.sticky;
    column.sticky ?
      this.addStickyColumnToUserPreferences(column) :
      this.handleColumnUnstick({ column, allColumns });

    return this.recalculateStickyPositions(allColumns, tableId);
  }

  private handleColumnUnstick({ column, allColumns }: StickyColumnParams): void {
    column.leftPositionForStickyColumn = undefined;
    this.removeStickyColumnFromUserPreferences(column);
    this.handleUnpinHiddenGroup({ column, allColumns });
  }

  private handleUnpinHiddenGroup({ column, allColumns }: StickyColumnParams): void {
    const groupId = column.columnProperties.GROUP?.id;
    if (!groupId) { return; }

    const groupColumns = allColumns.filter(c => c.columnProperties.GROUP?.id === groupId);
    const isGroupHidden = groupColumns.some(c => c.hiddenGroup);

    if (!isGroupHidden) { return; }

    this.processUnpinnedColumn({ column, groupColumns });
  }

  private processUnpinnedColumn(columnGroupParams: ColumnGroupParams): void {
    const { column } = columnGroupParams;
    column.hiddenGroup = true;
    const shouldSetPlaceholder = this.shouldColumnHavePlaceholder(columnGroupParams);
    if (!shouldSetPlaceholder) { return; }

    this.transferPlaceholderToColumn(columnGroupParams);
  }

  private shouldColumnHavePlaceholder({ column, groupColumns }: ColumnGroupParams): boolean {
    const firstVisibleColumn = groupColumns
      .filter(c => !c.hiddenColumn && !c.sticky)
      .sort((a, b) => a.order - b.order).at(0);

    return firstVisibleColumn?.key === column.key;
  }

  private transferPlaceholderToColumn({ column, groupColumns }: ColumnGroupParams): void {
    const currentPlaceholder = groupColumns.find(c => c.hiddenGroupPlaceholder);
    currentPlaceholder && (currentPlaceholder.hiddenGroupPlaceholder = false);

    column.hiddenGroupPlaceholder = true;
  }

  private recalculateStickyPositions(allColumns?: Array<RecordTableColumnParams>, tableId?: string): Array<RecordTableColumnParams> {
    if (!allColumns) { return this.stickyColumns; }

    this.updateStickyColumnsList(allColumns);
    this.calculateStickyPositions(tableId);
    this.clearNonStickyPositions(allColumns);

    return this.mergeStickyAndNonStickyColumns(allColumns);
  }

  private updateStickyColumnsList(allColumns: Array<RecordTableColumnParams>): void {
    this.stickyColumns = allColumns
      .filter(column => column.sticky)
      .sort((a, b) => a.order - b.order);
  }

  private calculateStickyPositions(tableId?: string): void {
    this.stickyColumns.reduce((offset, column) => {
      column.leftPositionForStickyColumn = offset;
      return offset + this.getColumnWidth(column, tableId);
    }, this.stickyLeftPosition());
  }

  private clearNonStickyPositions(allColumns: Array<RecordTableColumnParams>): void {
    allColumns.forEach(column => {
      if (!column.sticky) {
        column.leftPositionForStickyColumn = undefined;
      }
    });
  }

  private mergeStickyAndNonStickyColumns(allColumns: Array<RecordTableColumnParams>): Array<RecordTableColumnParams> {
    const nonStickyColumns = allColumns
      .filter(column => !column.sticky)
      .sort((a, b) => a.order - b.order);

    return [...this.stickyColumns, ...nonStickyColumns];
  }

  private getColumnWidth(column: RecordTableColumnParams, tableId?: string): number {
    return this.recordsTableService.getColumnWidth(column, tableId);
  }

  private addStickyColumnToUserPreferences(column: RecordTableColumnParams): void {
    const userPrefs = this.userPreferencesService.getUserPreferences() ?? {};
    const stickyKeys = userPrefs.stickyColumnKeys ?? [];

    if (!stickyKeys.includes(column.key)) {
      stickyKeys.push(column.key);
      this.userPreferencesService.saveUserPreferences({ stickyColumnKeys: stickyKeys });
    }
  }

  private removeStickyColumnFromUserPreferences(column: RecordTableColumnParams): void {
    const userPrefs = this.userPreferencesService.getUserPreferences();
    if (!userPrefs?.stickyColumnKeys) { return; }

    userPrefs.stickyColumnKeys = userPrefs.stickyColumnKeys.filter(k => k !== column.key);
    this.userPreferencesService.saveUserPreferences(userPrefs);
  }

  private stickyLeftPosition(): number {
    const key = this.userPreferencesService.tableKeyForUpdatingPreferences;
    if (!key) { return defaultLeftPosition; }

    return pagesWithTableCheckbox.includes(key) ?
      defaultLeftPositionWithCheckbox :
      defaultLeftPosition;
  }
}
